import { useLayoutEffect, useRef, type RefObject } from 'react';

/** Six lignes de vingt points, marges comprises : au-dela, la zone defile en elle-meme. */
const HAUTEUR_MAX_PX = 6 * 20 + 20;
/** Une ligne et ses marges : la hauteur d'une saisie vide. */
const HAUTEUR_MIN_PX = 40;

/**
 * Une zone de saisie qui grandit avec son texte, et ce qu'il y a au-dessus
 * qui monte avec elle.
 *
 * Un champ d'une ligne faisait sortir le debut du texte par la gauche, et
 * ne savait pas aller a la ligne. La zone prend donc la hauteur de ses
 * lignes, jusqu'a six ; au-dela, elle defile en elle-meme plutot que de
 * manger l'ecran. La messagerie et les commentaires s'en servent.
 *
 * # La hauteur se mesure sur une copie
 *
 * On ramenait la saisie a `auto` le temps de lire sa hauteur. Pendant cet
 * instant, le composeur perdait ses lignes et la zone defilante gagnait
 * d'autant : WebKit ramenait la lecture au nouveau bas, et elle y restait
 * quand la saisie reprenait sa taille. Releve sur le telephone : a chaque
 * lettre apres un retour a la ligne, le fil redescendait de vingt points
 * et le dernier message passait sous le composeur. La copie, hors du flux
 * et invisible, ne touche a rien.
 *
 * # La lecture monte avec la saisie
 *
 * La zone qui grandit prend sa hauteur a la zone defilante, par le bas.
 * On repose donc la distance au bas dans le meme passage, avant que
 * l'ecran ne se redessine : ce qu'on lisait reste colle au haut du
 * composeur, qu'il monte ou qu'il redescende. La distance au bas, et non
 * l'ecart ajoute : quand la saisie redescend, le navigateur recale deja
 * seul une lecture posee au bas.
 */
export function useSaisieExtensible(
  saisie: RefObject<HTMLTextAreaElement | null>,
  valeur: string,
  /** La zone qui defile au-dessus du composeur, s'il y en a une. */
  zoneDefilante: () => HTMLElement | null,
  /** Appelee quand la hauteur change, avant le defilement qu'elle provoque. */
  onRetaille?: () => void,
): void {
  const copieRef = useRef<HTMLTextAreaElement | null>(null);
  const rappel = useRef(onRetaille);
  rappel.current = onRetaille;
  const zoneRef = useRef(zoneDefilante);
  zoneRef.current = zoneDefilante;

  useLayoutEffect(() => {
    const zone = saisie.current;
    if (!zone) return;
    const vue = zoneRef.current();
    const avant = zone.offsetHeight;
    // Mesuree avant tout changement : c'est la distance qu'on veut garder.
    const auBas = vue ? vue.scrollHeight - vue.clientHeight - vue.scrollTop : 0;

    let copie = copieRef.current;
    if (!copie || copie.parentElement !== zone.parentElement) {
      copie?.remove();
      copie = document.createElement('textarea');
      copie.className = zone.className;
      copie.setAttribute('aria-hidden', 'true');
      copie.tabIndex = -1;
      copie.rows = 1;
      copie.style.cssText =
        'position:absolute;top:0;left:0;height:0;min-height:0;visibility:hidden;pointer-events:none;overflow:hidden;';
      zone.parentElement?.appendChild(copie);
      copieRef.current = copie;
    }
    copie.style.width = `${zone.offsetWidth}px`;
    copie.value = zone.value;
    const voulue = Math.min(Math.max(copie.scrollHeight, HAUTEUR_MIN_PX), HAUTEUR_MAX_PX);
    if (voulue === avant) return;

    zone.style.height = `${voulue}px`;
    if (!vue) return;
    rappel.current?.();
    vue.scrollTop = Math.max(0, vue.scrollHeight - vue.clientHeight - Math.max(0, auBas));
  }, [saisie, valeur]);
}
